// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Counting one training row.
 *
 * Three things here are the whole reason the tool is more correct than counting
 * the line with a chars-per-token ratio, and each is a place a competitor is
 * measurably wrong:
 *
 * 1. THE JSON WRAPPER IS NOT INPUT. A row is
 *    `{"messages":[{"role":"user","content":"hi"}]}` on disk, but the model is
 *    never shown the braces, the quotes, or the words `role` and `content`.
 *    Counting the raw line overcounts by the wrapper, and the overcount is
 *    largest on files with many short messages — which is what a chat dataset is.
 *
 * 2. THE FRAMING IS NOT FREE EITHER. Every message is wrapped by the chat format
 *    in tokens that ARE input and are not in `content`. Counting content alone
 *    undercounts. Only adding the framing gets it right.
 *
 * 3. TRUNCATION IS PER ROW, AND SO IS BILLING. See `billableTokens`.
 */

import type { Message, RowCount, RowProblem } from '../types';
import { ROLES } from '../types';

/**
 * OpenAI's documented chat-format accounting.
 *
 * Every message costs a fixed number of tokens beyond its content — the
 * `<|start|>`/`<|end|>` framing and the role — and every request ends with a
 * block priming the assistant's reply.
 *
 * These constants come from OpenAI's own cookbook
 * ("How to count tokens with tiktoken", `num_tokens_from_messages`) for the
 * gpt-4/gpt-4o family. They are DOCUMENTED ACCOUNTING RATHER THAN A PUBLISHED
 * SPECIFICATION: OpenAI has never published the framing for gpt-4.1, and the
 * cookbook itself warns the format "may change over time". The interface says
 * so rather than printing the word "exact" over a number with a modelled
 * component. The tokenizer half is exact; this half is documented.
 */
export const TOKENS_PER_MESSAGE = 3;
export const TOKENS_PER_NAME = 1;
export const REPLY_PRIMING = 3;

/** Encodes text to a token count. The only thing the counter needs from a tokenizer. */
export type CountText = (text: string) => number;

function roleIndex(role: unknown): number {
  const i = ROLES.indexOf(role as (typeof ROLES)[number]);
  // `other` is the last entry and is where an unrecognised role lands. It is a
  // real case: `function` rows from the pre-2023 format, and `developer` rows
  // from the current one, both appear in real training files.
  return i >= 0 && role !== 'other' ? i : ROLES.length - 1;
}

/**
 * Validate and count one parsed row.
 *
 * Returns a `problem` rather than throwing, because a single malformed line in a
 * two-million-row file must not end the run — and because "how many of my rows
 * are malformed" is itself one of the answers people come here for.
 */
export function countRow(
  value: unknown,
  countText: CountText,
  ordinal: number,
  offset: number,
  bytes: number,
): RowCount {
  const byRole = new Int32Array(ROLES.length);
  const fail = (problem: RowProblem): RowCount => ({
    ordinal,
    offset,
    bytes,
    tokens: 0,
    contentTokens: 0,
    byRole,
    messages: 0,
    problem,
  });

  if (value === null || typeof value !== 'object' || Array.isArray(value)) return fail('not-json');
  const messages = (value as { messages?: unknown }).messages;
  if (!Array.isArray(messages)) return fail('no-messages');
  if (messages.length === 0) return fail('empty-messages');

  let contentTokens = 0;
  let framing = REPLY_PRIMING;
  let sawAssistant = false;
  let sawNonString = false;

  for (const raw of messages) {
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return fail('message-not-object');
    const message = raw as Message;

    framing += TOKENS_PER_MESSAGE;
    // The role string itself is input. `name` both costs its own tokens and adds
    // a documented surcharge.
    if (typeof message.role === 'string') framing += countText(message.role);
    if (typeof message.name === 'string') framing += countText(message.name) + TOKENS_PER_NAME;

    if (message.role === 'assistant') sawAssistant = true;

    let text: string | null = null;
    if (typeof message.content === 'string') {
      text = message.content;
    } else if (Array.isArray(message.content)) {
      // The multi-part content format: [{type:'text', text:'…'}, …]. Only the
      // text parts are countable here; an image part's cost is a function of its
      // dimensions and is not knowable from the row, so it is reported as a
      // problem rather than guessed at.
      const parts: string[] = [];
      for (const part of message.content) {
        if (part && typeof part === 'object' && typeof (part as { text?: unknown }).text === 'string') {
          parts.push((part as { text: string }).text);
        } else {
          sawNonString = true;
        }
      }
      text = parts.join('');
    } else if (message.content !== null && message.content !== undefined) {
      sawNonString = true;
    }

    if (text !== null && text.length > 0) {
      const n = countText(text);
      contentTokens += n;
      byRole[roleIndex(message.role)] += n;
    }

    // A tool call is real input and is serialised into the prompt. Counting the
    // JSON is an approximation of the provider's own serialisation, but ignoring
    // it entirely is worse: a tool-calling dataset is mostly tool calls.
    if (message.tool_calls !== undefined && message.tool_calls !== null) {
      const n = countText(JSON.stringify(message.tool_calls));
      contentTokens += n;
      byRole[roleIndex(message.role)] += n;
    }
  }

  const problem: RowProblem | null = sawNonString
    ? 'content-not-string'
    : sawAssistant
      ? null
      : 'no-assistant';

  return {
    ordinal,
    offset,
    bytes,
    tokens: contentTokens + framing,
    contentTokens,
    byRole,
    messages: messages.length,
    problem,
  };
}

/**
 * Tokens the provider will actually charge you for.
 *
 * THE CAP IS THE POINT, and getting it wrong is the most expensive mistake this
 * tool could make. A row longer than the limit is truncated down to the limit
 * before training, so it is billed at the limit — not at its own length. The
 * obvious implementation, `sum(tokens) * epochs`, therefore overestimates by
 * exactly the amount of data that was thrown away, and it does so on precisely
 * the files this tool exists to find: the ones with over-limit rows.
 *
 * A 200,000-token row against a 65,536-token limit is billed at 65,536 and 134,464
 * tokens of it are deleted. Both numbers are shown, because they answer different
 * questions: one is what you pay, the other is what you lose.
 *
 * This matches OpenAI's own cookbook, which computes
 * `sum(min(MAX_TOKENS_PER_EXAMPLE, length) for length in convo_lens)`.
 */
export function billableTokens(rowTokens: readonly number[], limit: number, epochs: number): number {
  let total = 0;
  for (const t of rowTokens) total += Math.min(t, limit);
  return total * epochs;
}

/** Tokens deleted by truncation across the file — data you pay to prepare and never train on. */
export function truncatedTokens(rowTokens: readonly number[], limit: number): number {
  let total = 0;
  for (const t of rowTokens) if (t > limit) total += t - limit;
  return total;
}

/**
 * Cost in cents. `pricePerMillion` is dollars per 1,000,000 tokens.
 *
 * NOT rounded to whole cents. Rounding here was the first implementation and it
 * is wrong in the one direction that matters: a small file at a cheap LoRA rate
 * costs a fraction of a cent, Math.round takes it to 0, and the interface prints
 * "$0.00" — which reads as free rather than as cheap. Those are different
 * claims. The rounding belongs in the formatter, which can say "<$0.01".
 */
export function costCents(billable: number, pricePerMillion: number): number {
  return (billable / 1_000_000) * pricePerMillion * 100;
}

export function formatUsd(cents: number): string {
  const dollars = cents / 100;
  if (dollars > 0 && dollars < 0.005) return '<$0.01';
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
